<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const emit = defineEmits(['add']);

const { t } = useI18n();
const store = useStore();

const showDrawer = ref(false);

const state = reactive({
  id: '',
  title: '',
  description: '',
  instruction: '',
});

const rules = {
  title: { required, minLength: minLength(1) },
  description: { required },
  instruction: { required },
};

const v$ = useVuelidate(rules, state);

const titleError = computed(() =>
  v$.value.title.$error
    ? t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.TITLE.ERROR')
    : ''
);

const descriptionError = computed(() =>
  v$.value.description.$error
    ? t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.DESCRIPTION.ERROR')
    : ''
);

const instructionError = computed(() =>
  v$.value.instruction.$error
    ? t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.INSTRUCTION.ERROR')
    : ''
);

const resetState = () => {
  Object.assign(state, {
    id: '',
    title: '',
    description: '',
    instruction: '',
  });
  v$.value.$reset();
};

const openDrawer = () => {
  resetState();
  // 确保工具数据已加载
  store.dispatch('pilotTools/getTools').catch(() => {});
  showDrawer.value = true;
};

const closeDrawer = () => {
  showDrawer.value = false;
};

const onClickAdd = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  await emit('add', state);
  resetState();
  closeDrawer();
};

// 暴露方法供父组件调用
defineExpose({
  open: openDrawer,
  close: closeDrawer,
  togglePopover: val => {
    if (val) {
      openDrawer();
    } else {
      closeDrawer();
    }
  },
});
</script>

<template>
  <div class="inline-flex">
    <Button
      :label="t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.CREATE')"
      ghost
      sm
      class="flex-shrink-0 !text-n-slate-10 hover:!text-n-slate-12"
      @click="openDrawer"
    />

    <Teleport to="body">
      <!-- 遮罩层 -->
      <transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showDrawer"
          class="fixed inset-0 bg-black/50 z-[9998]"
          @click="closeDrawer"
        />
      </transition>

      <!-- 抽屉面板 -->
      <transition
        enter-active-class="transition-transform duration-300 ease-out"
        enter-from-class="-translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-x-0"
        leave-to-class="-translate-x-full"
      >
        <div
          v-if="showDrawer"
          class="fixed left-0 top-0 h-full w-[31.25rem] bg-n-solid-1 dark:bg-n-slate-900 shadow-2xl z-[9999] flex flex-col"
        >
          <!-- 头部 -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-slate-6 dark:border-n-slate-700"
          >
            <h3 class="text-lg font-medium text-n-slate-12">
              {{ t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.TITLE') }}
            </h3>
            <button
              class="p-1 rounded-lg hover:bg-n-slate-2 dark:hover:bg-n-slate-800 transition-colors"
              @click="closeDrawer"
            >
              <Icon icon="i-lucide-x" class="size-5 text-n-slate-11" />
            </button>
          </div>

          <!-- 内容区域 -->
          <div class="flex-1 overflow-y-auto px-6 py-4">
            <div class="flex flex-col gap-4">
              <Input
                v-model="state.title"
                :label="
                  t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.TITLE.LABEL')
                "
                :placeholder="
                  t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.TITLE.PLACEHOLDER')
                "
                :message="titleError"
                :message-type="titleError ? 'error' : 'info'"
              />

              <TextArea
                v-model="state.description"
                :label="
                  t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.DESCRIPTION.LABEL')
                "
                :placeholder="
                  t(
                    'PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.DESCRIPTION.PLACEHOLDER'
                  )
                "
                :message="descriptionError"
                :message-type="descriptionError ? 'error' : 'info'"
                show-character-count
              />

              <Editor
                v-model="state.instruction"
                :label="
                  t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.INSTRUCTION.LABEL')
                "
                :placeholder="
                  t(
                    'PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.INSTRUCTION.PLACEHOLDER'
                  )
                "
                :message="instructionError"
                :message-type="instructionError ? 'error' : 'info'"
                :show-character-count="false"
                enable-pilot-tools
                hide-copilot-button
                auto-height
              />

              <!-- 工具引用提示 -->
              <div class="flex items-center gap-2 text-sm text-n-slate-11">
                <Icon icon="i-lucide-info" class="size-4" />
                <span>{{ t('PILOT.ASSISTANTS.SCENARIOS.TOOLS.AT_TIP') }}</span>
              </div>
            </div>
          </div>

          <!-- 底部按钮 -->
          <div
            class="flex items-center gap-3 px-6 py-4 border-t border-n-slate-6 dark:border-n-slate-700"
          >
            <Button
              variant="faded"
              color="slate"
              :label="t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.CANCEL')"
              class="flex-1"
              @click="closeDrawer"
            />
            <Button
              :label="t('PILOT.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.CREATE')"
              class="flex-1"
              @click="onClickAdd"
            />
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>
